import React, { ReactNode } from "react";
import { ActiveElement, ChartEvent, ChartOptions } from "chart.js";
import { localize, roundToDigits, shorten } from "@backpacker/number";
import { Item, Item as Serie } from "@charty/legend/item/model";
import Tooltip from "@charty/tooltip/serie";
import { Color } from "@glossy";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { percent } from "@tootools/number";
import {
  closeCustomTooltip,
  openCustomTooltip,
} from "../tooltip/chartjs-tooltip-root";

type ChartTooltip = {
  serie: Item;
  datasetTotal: number;
};

export function getOptions(
  series: Serie[],
  locale: Locale,
  datasetTotal: number,
  centerLabel?: string,
  onClickPie?: (uid: Serie["id"]) => void,
  chartTooltip?: (props: ChartTooltip) => ReactNode,
  disableAnimation?: boolean,
  isFullscreen: boolean = false
): ChartOptions<"doughnut"> {
  return {
    animation: {
      duration: disableAnimation ? 0 : 1000,
    },
    maintainAspectRatio: false,
    onClick: function (evt) {
      if (onClickPie) {
        if (!isFullscreen) {
          closeCustomTooltip();
        }
        const elements = this.getElementsAtEventForMode(
          evt,
          "nearest",
          { intersect: true },
          false
        );
        if (elements && elements[0]) {
          onClickPie(series[elements[0].index].id);
        }
      }
    },
    cutout: "50%",
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
    plugins: {
      //@ts-ignore: datalabels is properly imported and registered on the chart
      datalabels: {
        font: {
          weight: "normal",
          size: 18,
          family: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        } as any,
        color: Color.White,
        anchor: "center" as any,
        display: function (ctx: any) {
          let total = 0;
          for (let i = 0; i < ctx.dataset.data.length; i++) {
            total += ctx.dataset.data[i] ? ctx.dataset.data[i] : 0;
          }
          return ctx.dataset.data[ctx.dataIndex]
            ? ctx.dataset.data[ctx.dataIndex] / total >= 0.06
            : false;
        },
        formatter: function (value) {
          const percentage = roundToDigits(percent(value, datasetTotal), 4);
          return shorten(locale, percentage, 3, false, 1) + "%";
        },
      },
      legend: { display: false },
      tooltip: {
        caretPadding: 24,
        enabled: isFullscreen,
        padding: 24,
        backgroundColor: Color.Black,
        titleColor: Color.White,
        titleFont: { size: 14 },
        bodySpacing: 12,
        bodyColor: Color.White,
        bodyFont: { size: 14 },
        callbacks: {
          label: (tooltipItem) => {
            return ` ${tooltipItem.label.toString() as string}`;
          },
          afterLabel: (tooltipItem) => {
            const _data = tooltipItem.chart.data.datasets[0].data as number[];
            const score = _data[tooltipItem.dataIndex] as number;
            const total = _data.reduce((acc, cur) => (acc += cur), 0);
            const percentage = shorten(locale, percent(score, total));
            return `${percentage}% - ${shorten(locale, score)}`;
          },
        },
        external: (context) => {
          if (isFullscreen) {
            return null;
          }
          const { chart, tooltip } = context;

          if (!tooltip.opacity) {
            closeCustomTooltip();
            return;
          }

          const dataPoints = tooltip.dataPoints;
          const serie = series[dataPoints[0].dataIndex];

          openCustomTooltip(
            tooltip,
            chart,
            chartTooltip ? (
              chartTooltip({ serie, datasetTotal })
            ) : (
              <Tooltip
                locale={locale}
                count={serie.count}
                label={serie.label}
                content={`${shorten(
                  locale,
                  roundToDigits(percent(serie.count, datasetTotal), 4),
                  3,
                  false,
                  1
                )}% - ${localize(locale, serie.count)} ${centerLabel}`}
                color={serie.color}
                total={datasetTotal}
              />
            )
          );
        },
      },
    },
    onHover: (e: ChartEvent, elements: ActiveElement[]) => {
      if (onClickPie && e.native.target instanceof HTMLElement) {
        const cursor = elements.length ? "var(--cursor-magic-wand)" : "default";
        if (e.native.target.style.cursor !== cursor) {
          e.native.target.style.cursor = cursor;
          e.native.target.dispatchEvent(getOnChangeCanvasCursorEvent());
        }
      }
    },
  };
}
